// Neon client (Managed Better Auth + Data API) behind the old `supabase` export, so call sites keep
// their Supabase shapes: supabase.auth.*, supabase.from(...), supabase.storage.from(...).
import { createClient, SupabaseAuthAdapter } from '@neondatabase/neon-js'
import { createStorage, installExpiredLinkRetry } from './storage'
import { createRetryFetch } from './retryFetch'

const client = createClient({
  auth: { url: process.env.REACT_APP_NEON_AUTH_URL, adapter: SupabaseAuthAdapter() },
  dataApi: {
    url: process.env.REACT_APP_NEON_DATA_API_URL,
    options: { global: { fetch: createRetryFetch() } },
  },
})

// iCal sync ran on a Supabase Edge Function, which doesn't exist on Neon. Off unless explicitly enabled.
export const ICAL_SYNC_ENABLED = process.env.REACT_APP_FEATURE_ICAL_SYNC === 'true'

// neon-js 0.7.0-beta only delivers onAuthStateChange events to *other* tabs (it skips broadcasts from
// its own tab id), so the tab that signs in or out never hears about it. Notify same-tab listeners here.
const listeners = new Set()

function emit(event, session) {
  listeners.forEach(cb => cb(event, session))
}

async function emitCurrentSession(event) {
  const { data } = await client.auth.getSession({ forceFetch: true })
  emit(event, data?.session ?? null)
}

const auth = {
  getSession: (options) => client.auth.getSession(options),
  resend: (params) => client.auth.resend(params),
  signInWithOAuth: (params) => client.auth.signInWithOAuth(params),

  async signInWithPassword(credentials) {
    const result = await client.auth.signInWithPassword(credentials)
    if (!result.error) await emitCurrentSession('SIGNED_IN')
    return result
  },

  async signUp(credentials) {
    const result = await client.auth.signUp(credentials)
    if (!result.error) await emitCurrentSession('SIGNED_IN')
    return result
  },

  async verifyOtp(params) {
    const result = await client.auth.verifyOtp(params)
    if (!result.error) await emitCurrentSession(params.type === 'email' ? 'SIGNED_IN' : 'USER_UPDATED')
    return result
  },

  // "Email me a code" sign-in. The adapter's signInWithOtp() ignores a failed send and reports success,
  // so call the Better Auth endpoint directly and surface its error.
  async sendSignInCode(email) {
    try {
      const res = await fetch(`${process.env.REACT_APP_NEON_AUTH_URL}/email-otp/send-verification-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'sign-in' }),
      })
      if (res.ok) return { error: null }
      const body = await res.json().catch(() => ({}))
      return { error: new Error(body.message || `Couldn't send a code (${res.status})`) }
    } catch {
      return { error: new Error("Couldn't send a code. Check your connection and try again.") }
    }
  },

  // Sign in with the emailed code.
  signInWithCode: (email, code) => auth.verifyOtp({ email, token: code, type: 'email' }),

  async signOut() {
    const result = await client.auth.signOut()
    emit('SIGNED_OUT', null)
    return result
  },

  onAuthStateChange(callback) {
    listeners.add(callback)
    const { data } = client.auth.onAuthStateChange(callback)
    return {
      data: {
        subscription: {
          unsubscribe() {
            listeners.delete(callback)
            data.subscription.unsubscribe()
          },
        },
      },
    }
  },
}

installExpiredLinkRetry(() => client.auth.getSession())

export const supabase = {
  auth,
  from: (table) => client.from(table),
  rpc: (fn, args) => client.rpc(fn, args),
  storage: createStorage(() => client.auth.getSession()),
  functions: {
    invoke: async () => ({ data: null, error: new Error('iCal sync is not available') }),
  },
}
